export type Tome = {
  label: string;
  emoji: string;
  title: string;
  subtitle: string;
  accent: 'blue' | 'green' | 'gold';
  cover: string;
  coverAlt: string;
  features: string[];
  note?: string;
  warning?: string;
};

export type Bonus = {
  number: string;
  title: string;
  pages: string;
  description: string;
  cover: string;
  coverAlt: string;
  features: string[];
};

/** Los 3 tomos principales del Kit Maestro. */
export const tomes: Tome[] = [
  {
    label: 'TOMO I',
    emoji: '📘',
    title: 'Blindaje Legal 360°',
    subtitle: 'Documentación, límites profesionales y buenas prácticas para tu consulta',
    accent: 'blue',
    cover: 'https://cdn.magicpatterns.com/uploads/oE5jFxLc2rreviCtEDD59t/tomo_1.png',
    coverAlt: 'Portada del Tomo I: Blindaje Legal 360°',
    features: [
      'Consentimiento informado editable',
      'Deslinde de responsabilidad',
      'Acuerdo de confidencialidad',
      'Criterios de derivación',
      'Comunicación profesional con consultantes',
      'Límites entre acompañamiento y atención sanitaria',
      'Preguntas frecuentes sobre documentación y práctica',
    ],
    note: '🛡️ 4 documentos editables incluidos. Aprende a establecer desde el comienzo qué haces, qué no haces y cuál es el alcance de tu acompañamiento.',
    warning:
      'IMPORTANTE PARA LATINOAMÉRICA: los documentos son modelos educativos y deben adaptarse y revisarse según la legislación y los requisitos profesionales de tu país.',
  },
  {
    label: 'TOMO II',
    emoji: '📗',
    title: 'Protocolos de Bioshock y Anamnesis Express',
    subtitle: 'Una estructura práctica para organizar la exploración del contexto personal y emocional',
    accent: 'green',
    cover: 'https://cdn.magicpatterns.com/uploads/t6h4wEDRCo3RGfGDQxVwbu/Screenshot_20260908-205643~2.jpg',
    coverAlt: 'Portada del Tomo II: Protocolos de Bioshock y Anamnesis Express',
    features: [
      'Fundamentos del enfoque Bioshock',
      'Conceptos esenciales del sistema nervioso autónomo',
      '7 preguntas para ordenar la exploración',
      'Mapa visual del cuerpo',
      'Árbol genealógico flash',
      'Identificación de acontecimientos y patrones repetidos',
      'Scripts orientativos para diferentes momentos de la sesión',
      'Casos prácticos de aplicación',
    ],
    note: '🧠 Ten una ruta cuando no sepas cuál debería ser la próxima pregunta.',
  },
  {
    label: 'TOMO III',
    emoji: '📕',
    title: 'Prácticas Simbólicas de Integración',
    subtitle: 'Recursos concretos para darle estructura al cierre de la sesión',
    accent: 'gold',
    cover: 'https://cdn.magicpatterns.com/uploads/cATMsnKzgCLq5KMaYi6CB8/Screenshot_20260908-205730~2.jpg',
    coverAlt: 'Portada del Tomo III: Prácticas Simbólicas de Integración',
    features: [
      '15 prácticas guiadas listas para proponer',
      '✍️ Escritura reflexiva',
      '🧠 Visualización',
      '🌿 Respiración y regulación',
      '🧘 Movimiento consciente',
      '💬 Reflexión personal',
      '✅ Ejercicios de cierre',
      'Cómo seleccionar una práctica según el objetivo trabajado',
      'Cómo realizar seguimiento posteriormente',
    ],
    note: 'Porque profundizar es importante. Pero saber cerrar también lo es.',
  },
];

/** Los 5 bonus estratégicos, siempre incluidos. */
export const bonuses: Bonus[] = [
  {
    number: '01',
    title: 'El Cuaderno del Paciente',
    pages: 'Envíalo antes de la primera sesión',
    description:
      'Tu consultante organiza su información antes de llegar, y tú aprovechas mejor el tiempo de consulta.',
    cover: 'https://cdn.magicpatterns.com/uploads/5finiQZ4aqkPnvg1Kw64F5/1.png',
    coverAlt: 'Portada del Bonus 01: El Cuaderno del Paciente',
    features: [
      'Historia personal',
      'Antecedentes familiares',
      'Acontecimientos importantes',
      'Línea de tiempo',
      'Relaciones',
      'Motivo de consulta',
    ],
  },
  {
    number: '02',
    title: 'Bitácora de 21 Días',
    pages: 'Herramienta post-sesión',
    description:
      'En lugar de dejar todo librado a la memoria, el consultante dispone de una estructura para registrar su experiencia.',
    cover: 'https://cdn.magicpatterns.com/uploads/q6UweopNBgyHAyVsGaT7PZ/ChatGPT_Image_7_sept_2026,_18_01_29.png',
    coverAlt: 'Portada del Bonus 02: Bitácora de 21 Días',
    features: ['Emociones', 'Sueño', 'Hábitos', 'Observaciones', 'Sensaciones', 'Cambios percibidos'],
  },
  {
    number: '03',
    title: 'Radar de Preguntas Gatillo™',
    pages: '50 preguntas',
    description:
      '¿Te quedaste en blanco? Abre el Radar. Una herramienta visual para tener abierta al lado durante tus sesiones.',
    cover: 'https://cdn.magicpatterns.com/uploads/tBkt9j9YAkJNCJNn4Vb4f3/1.png',
    coverAlt: 'Portada del Bonus 03: Radar de Preguntas Gatillo',
    features: [
      '🧠 Contexto',
      '❤️ Vínculos',
      '⏱️ Cronología',
      '💭 Emociones',
      '🌳 Historia familiar',
      '🔄 Repeticiones',
    ],
  },
  {
    number: '04',
    title: 'Guía de Comunicación Ética para Meta Ads',
    pages: 'Facebook e Instagram',
    description:
      'Si además promocionas tus servicios en redes, este bonus te ayuda a comunicar tu propuesta con mayor claridad.',
    cover: 'https://cdn.magicpatterns.com/uploads/q34MrRM3qQUZe6QLCwFoRE/ChatGPT_Image_8_sept_2026,_11_13_55.png',
    coverAlt: 'Portada del Bonus 04: Guía de Comunicación Ética para Meta Ads',
    features: [
      'Lenguaje recomendado',
      'Expresiones que conviene evitar',
      'Ejemplos de comunicación',
      'Buenas prácticas publicitarias',
      'Enfoque profesional de tus servicios',
    ],
  },
  {
    number: '05',
    title: 'Lista de Chequeo “Filtro Rojo”',
    pages: 'Guía visual',
    description:
      'Profesionalizar tu práctica también significa saber cuándo detenerte y cuándo recomendar asistencia de un profesional habilitado.',
    cover: 'https://cdn.magicpatterns.com/uploads/pdXLmdsTtqiCj347SWauxt/ChatGPT_Image_8_sept_2026,_11_48_27.png',
    coverAlt: 'Portada del Bonus 05: Lista de Chequeo Filtro Rojo',
    features: [
      'Señales de alerta',
      'Límites del acompañamiento',
      'Criterios de derivación',
      'Qué registrar',
      'Cómo comunicar una derivación',
    ],
  },
];
